import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CreditCard, LogIn, User, Search, GitCompare, Trophy, ArrowLeftRight, Briefcase, Calculator, Home, Menu } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Logo } from './Logo';

export function Header({ session }: { session: any }) {
  const location = useLocation();

  const desktopNav = [
    { path: '/', label: 'Terminal', icon: Home },
    { path: '/rankings', label: 'Rankings', icon: Trophy },
    { path: '/compare', label: 'Comparador', icon: ArrowLeftRight },
    { path: '/portfolio', label: 'Carteira', icon: Briefcase },
    { path: '/juros-compostos', label: 'Simulador', icon: Calculator },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-[9998] bg-[#FAF8F5]/95 dark:bg-[#0A0C10]/95 backdrop-blur-md border-b border-[#E4E0D8] dark:border-[#1F2430] px-3 sm:px-6 lg:px-8 h-14 flex items-center print:hidden">
      <div className="w-full max-w-7xl mx-auto flex justify-between items-center gap-2 sm:gap-3 xl:gap-4">
        {/* Left: Integrated Menu Trigger + Brand Identity + Desktop Navigation Tabs */}
        <div className="flex items-center gap-2.5 sm:gap-4 xl:gap-5 shrink-0">
          <button
            type="button"
            onClick={() => document.dispatchEvent(new CustomEvent('toggle-sidebar-drawer'))}
            className="w-9 h-9 shrink-0 flex items-center justify-center bg-white dark:bg-[#11141C] border border-[#E4E0D8] dark:border-[#1F2430] hover:border-[#CFC9BC] dark:hover:border-[#2E3646] rounded text-[#111315] dark:text-[#F3F4F6] transition-colors cursor-pointer"
            aria-label="Abrir índice de navegação"
            title="Índice de Navegação"
          >
            <Menu className="w-4 h-4" />
          </button>

          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group shrink-0 whitespace-nowrap">
            <div className="w-7 h-7 shrink-0 bg-white dark:bg-[#11141C] border border-[#E4E0D8] dark:border-[#1F2430] rounded flex items-center justify-center">
              <Logo className="w-4 h-4" />
            </div>
            <div className="hidden min-[360px]:flex items-baseline gap-2">
              <span className="font-display font-bold text-base sm:text-lg tracking-tight text-[#111315] dark:text-[#F3F4F6] whitespace-nowrap">
                Valor Justo
              </span>
              <span className="hidden 2xl:inline-block font-mono text-[10px] uppercase tracking-widest text-[#5A6065] dark:text-[#8A94A6] border-l border-[#E4E0D8] dark:border-[#1F2430] pl-2 whitespace-nowrap">
                EQUITY RESEARCH
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center h-14 border-l border-[#E4E0D8] dark:border-[#1F2430] pl-3 xl:pl-4 gap-0.5 xl:gap-1 shrink-0">
            {desktopNav.map((item) => {
              const isActive = location.pathname === item.path || (item.path === '/' && location.pathname.startsWith('/acao/'));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative h-14 px-2.5 xl:px-3 flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase whitespace-nowrap shrink-0 transition-colors ${
                    isActive
                      ? 'text-[#111315] dark:text-[#F3F4F6]'
                      : 'text-[#5A6065] dark:text-[#8A94A6] hover:text-[#111315] dark:hover:text-[#F3F4F6]'
                  }`}
                >
                  <span>{item.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#0D7A5F] dark:bg-[#10B981]" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Right: Quick Tools & Account Controls */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 whitespace-nowrap ml-auto">
          <button
            type="button"
            onClick={() => document.dispatchEvent(new CustomEvent('open-quick-compare'))}
            className="flex items-center justify-center h-9 gap-1.5 px-2.5 text-xs font-medium text-[#5A6065] dark:text-[#8A94A6] hover:text-[#111315] dark:hover:text-[#F3F4F6] bg-white dark:bg-[#11141C] border border-[#E4E0D8] dark:border-[#1F2430] hover:border-[#CFC9BC] dark:hover:border-[#2E3646] rounded transition-colors shrink-0 whitespace-nowrap cursor-pointer"
            title="Comparação Rápida de Ativos (Duelo)"
            aria-label="Comparação Rápida de Ativos"
          >
            <GitCompare className="w-3.5 h-3.5 shrink-0 text-[#0D7A5F] dark:text-[#10B981]" />
            <span className="hidden sm:inline lg:hidden xl:inline font-mono uppercase tracking-wider text-[11px]">
              Duelo
            </span>
          </button>

          <button
            type="button"
            onClick={() => document.dispatchEvent(new CustomEvent('open-command-palette'))}
            className="flex items-center justify-center h-9 gap-1.5 xl:gap-2 px-2.5 text-xs font-medium text-[#5A6065] dark:text-[#8A94A6] hover:text-[#111315] dark:hover:text-[#F3F4F6] bg-white dark:bg-[#11141C] border border-[#E4E0D8] dark:border-[#1F2430] hover:border-[#CFC9BC] dark:hover:border-[#2E3646] rounded transition-colors shrink-0 whitespace-nowrap cursor-pointer"
            title="Busca Rápida (Ctrl+K)"
            aria-label="Busca Rápida (Ctrl+K)"
          >
            <Search className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden md:inline lg:hidden 2xl:inline text-xs text-[#5A6065] dark:text-[#8A94A6]">
              Buscar ativo...
            </span>
            <kbd className="hidden sm:inline font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F3F0EA] dark:bg-[#161A24] text-[#5A6065] dark:text-[#8A94A6] border border-[#E4E0D8] dark:border-[#1F2430]">
              ⌘K
            </kbd>
          </button>

          <div className="w-px h-5 bg-[#E4E0D8] dark:bg-[#1F2430] mx-0.5 shrink-0" />

          <div className="shrink-0">
            <ThemeToggle />
          </div>

          <Link
            to="/pricing"
            title="Planos & Assinatura"
            className="flex items-center justify-center h-9 gap-1.5 px-2.5 text-xs font-mono uppercase tracking-wider font-semibold text-[#111315] dark:text-[#F3F4F6] bg-[#F3F0EA] dark:bg-[#161A24] hover:bg-[#E4E0D8]/70 dark:hover:bg-[#1F2430] border border-[#E4E0D8] dark:border-[#1F2430] rounded transition-colors shrink-0 whitespace-nowrap cursor-pointer"
          >
            <CreditCard className="w-3.5 h-3.5 shrink-0 text-[#0D7A5F] dark:text-[#10B981]" />
            <span className="hidden sm:inline lg:hidden xl:inline">Planos</span>
          </Link>

          {session ? (
            <Link
              to="/profile"
              title="Meu Perfil"
              className="flex items-center justify-center h-9 gap-2 pl-1.5 pr-2 sm:pr-2.5 text-xs font-semibold text-[#111315] dark:text-[#F3F4F6] bg-white dark:bg-[#11141C] border border-[#E4E0D8] dark:border-[#1F2430] hover:border-[#CFC9BC] dark:hover:border-[#2E3646] rounded transition-colors shrink-0 whitespace-nowrap cursor-pointer"
            >
              {session.user_metadata?.avatar_url ? (
                <img
                  src={session.user_metadata.avatar_url}
                  alt="Perfil"
                  className="w-6 h-6 shrink-0 rounded-sm object-cover border border-[#E4E0D8] dark:border-[#1F2430]"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-6 h-6 shrink-0 rounded-sm bg-[#111315] dark:bg-[#F3F4F6] flex items-center justify-center text-white dark:text-[#0A0C10]">
                  <User className="w-3.5 h-3.5" />
                </div>
              )}
              <span className="hidden sm:inline font-mono text-[11px] tracking-tight truncate max-w-[76px] xl:max-w-[104px]">
                {session.user_metadata?.username || session.user_metadata?.full_name?.split(' ')[0] || 'PERFIL'}
              </span>
            </Link>
          ) : (
            <Link
              to="/login"
              title="Entrar na conta"
              className="flex items-center justify-center h-9 gap-1.5 px-2.5 sm:px-3 text-xs font-mono uppercase tracking-wider font-semibold text-white dark:text-[#0A0C10] bg-[#111315] dark:bg-[#F3F4F6] hover:opacity-90 rounded transition-opacity shrink-0 whitespace-nowrap cursor-pointer"
            >
              <LogIn className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">Entrar</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

